<template>
  <b-container class="p-0 mt-3">
    <div class="d-flex flex-column">
      <b-col class="text-center col-12">
        <b-row class="mt-1 mb-2 align-items-center d-flex justify-content-center">
          <b-col class="p-0 col-10">
            <b-img
                v-if="image"
                :class="{'zoom-in' : enableZoomIn}"
                @click="onImageZoom"
                :src="image"
                :style="[defaultStyle]"
                alt="img"/>

            <!--          DRAG AND DROP                  -->
            <b-card
                v-else
                class="image-card"
                :style="[dragOverStyle]">
              <div class="card-content">
                <img src="https://silkplm.com/wp-content/uploads/2025/02/image.png" alt="Uploaded Image"
                     class="upload-image"/>
                <div class="text-left mt-3 w-100 pr-5 pl-5">
                  <span class="instruction-title">
                      {{ $tr('instruction-title-inspiration', 'To generate a description:') }}
                  </span>
                  <div class="w-100 d-flex flex-row align-items-center mb-2 mt-2">
                    <div style="width: 10%" class=" pr-2 d-flex justify-content-center align-items-center">
                      <font-awesome-icon class="upload-icon" :icon="['far', 'file-arrow-up']"/>
                    </div>
                    <div style="width: 90%">
                      <span class="instruction-span">
                        {{ $tr('instruction-instruction-span-inspiration-no-1', 'Upload an image in the') + " "}}
                        <span class="instruction-span custom-bold">
                          {{ $tr('instruction-instruction-span-inspiration-no-2', 'Design Tab') }}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div class="w-100 d-flex flex-row align-items-center mb-2">
                    <div style="width: 10%" class="w-20 pr-2 d-flex justify-content-center align-items-center">
                      <font-awesome-icon class="upload-icon" :icon="['far', 'star-half-stroke']"/>
                    </div>
                    <div style="width: 90%" class="w-80">
                      <span class="instruction-span">
                        {{ $tr('instruction-instruction-span-inspiration-no-3', 'Select the star icon to set the image as the main photo') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <!--          BUTTONS                  -->
        <b-row class="align-items-center d-flex justify-content-center">
          <b-col class="p-0 col-10 align-items-center d-flex justify-content-center">
            <b-row class="w-100 align-items-center d-flex justify-content-center">
              <!--          AI DESCRIPTION                  -->
              <b-col cols="2" md="6" class="mb-2 pr-1 pl-0" v-if="!readOnly">
                <b-button
                    class="inspiration-button"
                    variant="plm-action"
                    data-test-path="ai-description-generate-btn"
                    @click="generateDescription"
                    :disabled="loading || !image"
                >
                  <font-awesome-icon v-if="!loading" icon="fa-regular fa-microchip-ai"/>
                  <span class="d-none d-md-inline">
                    {{ $tr('generate-description-ai-button', 'Description by AI') }}
                  </span>
                  <font-awesome-icon :icon="['fad', 'spinner-third']" v-if="loading && !creatingProduct" spin
                                     style="--fa-primary-color: #ffffff; --fa-secondary-color: #ffffff;"/>
                </b-button>
              </b-col>

              <!--          CLEAR INPUTS                  -->
              <b-col cols="2" md="6" class="mb-2 pl-1 pr-0" v-if="!readOnly">
                <b-button
                    class="inspiration-button"
                    variant="plm-action"
                    @click="clearInputs"
                    :disabled="loading || !notEmptyFields"
                >
                  <font-awesome-icon icon="broom-wide"/>
                  <span class="d-none d-md-inline">
                    {{ $tr('clear-inputs-button', 'Clear Data') }}
                  </span>
                </b-button>
              </b-col>
              <!--          DO PHOTO                  -->
              <b-col cols="6" class="mb-2 pl-0 pr-1" v-if="!readOnly && checkIsCordova">
                <b-button
                    class="inspiration-button"
                    variant="plm-action"
                >
                  <font-awesome-icon icon="fa-camera"/>
                  <span class="d-none d-md-inline">
                    {{ $tr('inspiration-camera-button', 'Do Photo') }}
                  </span>
                </b-button>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-col>
    </div>
  </b-container>
</template>

<script>

import FileUpload from "@/components/FileUpload";
import FormValidatedField from "@/components/forms/FormValidatedField";
import ActivityManager from "@/activity/ActivityManager";
import RpcInputValidationException from "@/server/com/silkplm/engine/rpc/exceptions/RpcInputValidationException";
import Loading from "@/components/layout/Loading.vue";
import FormsLoader from "@/components/forms/FormsLoader";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";
import FormsActions from "@/components/forms/FormsActions";
import ImageGallery from "@/server/com/silkplm/files/images/ImageGallery";
import ListOfImageTag from "@/server/com/silkplm/rpc/plm/ListOfImageTag";
import Rpc from "@/Rpc";
import DialogManager from "@/utils/DialogManager";
import SimpleActivity from "@/activity/templates/layouts/SimpleActivity.vue";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import Product from "@/server/com/silkplm/model/plm/Product";

const PinturaImageEditor = () => require("@/components/img/PinturaImageEditor");
const SlideShow = () => require("@/components/img/SlideShow");

const AIGenerator = () => require("@/components/tools/AIPhotoGenerator/AIProductGenerator.vue")
export default {
  name: "InspirationRenderer",
  components: {SimpleActivity, Loading, FileUpload},

  mounted() {},

  computed: {
    checkIsCordova() {
      return window.isCordova
    },

    addCordovaUrl() {
      return window.cordovaUrl
    },

    notEmptyFields() {
      return this.validatedField.validatedForm.validatedFieldByName('reachDescription').value ||
          this.validatedField.validatedForm.validatedFieldByName('name').value ||
          this.validatedField.validatedForm.validatedFieldByName('color').value ||
          this.validatedField.validatedForm.validatedFieldByName('productAdvertise').value ||
          this.validatedField.validatedForm.validatedFieldByName('useRecommendation').value;
    }
  },

  methods: {
    setReadOnly(readOnly) {
      this.readOnly = this.fieldConfig.readOnly || readOnly;
    },

    setEditorMode(val) {
      this.editorMode = val;
    },

    setImageAsNull() {
      this.validatedField.value = null;
      this.image = null;
    },

    async clearInputs() {
      let clearData = true;
      if (this.notEmptyFields) {
        clearData = await DialogManager.yesNo($tr('inspiration-renderer-clear-data-title', 'Clear Inputs'),
            $tr('inspiration-renderer-clear-data', 'Are you sure that you want to clear inputs?'));
      }
      if (clearData) {
        this.clearing();
      }
    },

    clearing() {
      const validatedForm = this.validatedField.validatedForm;

      // Sprawdzenie i ewentualne oczekiwanie na rozwiązanie Promise
      validatedForm.validatedFieldByName('name').value = '';
      //validatedForm.validatedFieldByName('color').value = '';
      validatedForm.validatedFieldByName('reachDescription').value = '';
      validatedForm.validatedFieldByName('productAdvertise').value = '';
      validatedForm.validatedFieldByName('useRecommendation').value = '';
    },

    async generateDescription() {
      this.loading = true;
      this.clearing();
      this.validatedField.validatedForm.validatedFieldByName('name').renderer.readOnly = true;
      //this.validatedField.validatedForm.validatedFieldByName('color').renderer.readOnly = true;
      this.validatedField.validatedForm.validatedFieldByName('reachDescription').renderer.readOnly = true;
      this.validatedField.validatedForm.validatedFieldByName('productAdvertise').renderer.readOnly = true;
      this.validatedField.validatedForm.validatedFieldByName('useRecommendation').renderer.readOnly = true;

      let makeSure = true;
      if (this.notEmptyFields) {
        makeSure = await DialogManager.yesNo($tr('image-viewer-desc-override-title', 'Overriding Inputs'),
            $tr('inspiration.image-viewer-desc-override', 'Are you sure you want to override inputs?'))
        this.loading = makeSure;
      }
      try {
        let task = await Rpc.ai.GenerateDescriptionByAI.getImageDescriptionByAiV2(SessionManager.defaultCustomerLanguageCode, "", this.validatedField.value, false);
        this.descriptionAI = await task.finish();
        if (this.descriptionAI) {
          //Tutaj było this.descriptionAI.is_product - sprawdzenie Jenkins
          if (makeSure && this.descriptionAI.title) {
            this.clearing();

            this.validatedField.validatedForm.validatedFieldByName('name').value = this.descriptionAI.title.charAt(0).toUpperCase() + this.descriptionAI.title.slice(1);
            await this.changeValidatedFieldValueIsEmpty('name');

            // this.validatedField.validatedForm.validatedFieldByName('type').value = this.descriptionAI.type.charAt(0).toUpperCase() + this.descriptionAI.type.slice(1);
            // await this.changeValidatedFieldValueIsEmpty('type');

            // let valueOfColor = this.validatedField.validatedForm.validatedFieldByName('color').value;
            //
            // await this.changeValidatedFieldValueIsEmpty('color');
            // const {r, g, b, color_name} = this.descriptionAI.color;
            //
            // const pantone = await Rpc.Pantone.pantoneByRGBInFamily(r, g, b, "Cotton");
            // if (pantone == null) {
            //   valueOfColor = `${valueOfColor.charAt(0).toUpperCase() + valueOfColor.slice(1)} (R: ${r}, G: ${g}, B: ${b})`;
            // }
            //

            // this.validatedField.validatedForm.validatedFieldByName('color').value = color_name + ', ' + pantone.pantone;
            // if (!this.validatedField.validatedForm.validatedFieldByName('color').value) {
            //   this.changeValidatedFieldValueIsEmpty('color');
            // }

            // if (this.descriptionAI.main_pattern && this.descriptionAI.main_pattern.pattern_name) {
            //   this.validatedField.validatedForm.validatedFieldByName('mainPattern').value = this.descriptionAI.main_pattern.pattern_name.charAt(0).toUpperCase() + this.descriptionAI.main_pattern.pattern_name.slice(1);
            // } else {
            //   await this.changeValidatedFieldValueIsEmpty('mainPattern');
            // }

            this.validatedField.validatedForm.validatedFieldByName('reachDescription').value = this.descriptionAI.description.charAt(0).toUpperCase() + this.descriptionAI.description.slice(1);
            await this.changeValidatedFieldValueIsEmpty('reachDescription');
            this.validatedField.validatedForm.validatedFieldByName('productAdvertise').value = this.descriptionAI.content.charAt(0).toUpperCase() + this.descriptionAI.content.slice(1);
            await this.changeValidatedFieldValueIsEmpty('productAdvertise');
            this.validatedField.validatedForm.validatedFieldByName('useRecommendation').value = this.descriptionAI.usage.charAt(0).toUpperCase() + this.descriptionAI.usage.slice(1);
            await this.changeValidatedFieldValueIsEmpty('useRecommendation');
          }
        }
      } catch (e) {
        console.log(e);
      }
      //Tutaj było !this.descriptionAI.is_product
      if (this.descriptionAI) {
        if (!this.descriptionAI.is_product) {
          this.changeValidatedFieldValueIsEmpty('reachDescription');
          this.changeValidatedFieldValueIsEmpty('name');
          // this.changeValidatedFieldValueIsEmpty('color');
          this.changeValidatedFieldValueIsEmpty('productAdvertise');
          this.changeValidatedFieldValueIsEmpty('useRecommendation');
        } else {
          this.changeValidatedFieldValueIsEmpty('reachDescription');
          this.changeValidatedFieldValueIsEmpty('name');
          // this.changeValidatedFieldValueIsEmpty('color');
          this.changeValidatedFieldValueIsEmpty('productAdvertise');
          this.changeValidatedFieldValueIsEmpty('useRecommendation');
        }
      }
      this.loading = false;
      this.validatedField.validatedForm.validatedFieldByName('reachDescription').renderer.readOnly = false;
      this.validatedField.validatedForm.validatedFieldByName('name').renderer.readOnly = false;
      // this.validatedField.validatedForm.validatedFieldByName('color').renderer.readOnly = false;
      this.validatedField.validatedForm.validatedFieldByName('productAdvertise').renderer.readOnly = false;
      this.validatedField.validatedForm.validatedFieldByName('useRecommendation').renderer.readOnly = false;
    },

    async changeValidatedFieldValueIsEmpty(name) {
      if (!this.validatedField.validatedForm.validatedFieldByName(name).value) {
        this.validatedField.validatedForm.validatedFieldByName(name).value = await this.translate('Nothing Found');
      }
    },

    async translate(input) {
      return await Rpc.ai.GenerateDescriptionByAI.translateToOrganizationLanguage(SessionManager.defaultCustomerLanguageCode, input);
    },

    async imageZoom() {
      let slideShow = await ActivityManager.showActivity(SlideShow, {
        image: this.validatedField.value,
        singleImageEditor: true,
        uploadImage: async (image) => await Rpc.BusinessEntities.uploadImage(image).lock()
      });
      slideShow.$on("new-image", (image) => {
        this.validatedField.value = image;
      });
    },

    async imageUploadSelected() {
      try {
        this.validatedField.value = await Rpc.BusinessEntities.uploadImage(file, "").lock().onProgress((e) => {
        });
        this.image = this.validatedField.value.url;
      } catch (e) {
        console.error(e)
      }
    },

    async editImage() {
      let result = await (await ActivityManager.showActivity(PinturaImageEditor, {
        image: this.validatedField.value,
        singleImageEditor: true,
        uploadImage: async (image) => await Rpc.BusinessEntities.uploadImage(image, "").lock()
      })).getResult();

      if (result) {
        this.validatedField.value = result.newFile;
        this.image = result.newFile.url;
      }
    },

    async onImageZoom() {
      await this.imageZoom();
    },

    async onDeleteImage() {
      if (await DialogManager.yesNo($tr('image-viewer-sure-delete-title', 'Deleting Image'),
          $tr('image-viewer-sure-delete', 'Are you sure that you want to delete this image?'))) {
        this.setImageAsNull();
      }
    },


  },

  props: {
    validatedField: {
      type: FormValidatedField, required: true
    },
    fieldConfig: {
      type: Object, required: true
    },
    context: {default: null},
    mode: {default: null},
  },

  watch: {
    image: function (val) {
      if(val === null){
        this.imageDragOver = true;
      }
    },

    validatedField: function (value) {
      this.image = this.validatedField?.value?.url;
    },

    "validatedField.value": function (value) {
      this.image = this.validatedField?.value?.url;
    }
  },

  data: function () {
    return {
      loading: false,
      creatingProduct: false,
      readOnly: true,
      editorMode: false,
      edit: false,
      zoom: false,
      remove: false,
      enableZoomIn: false,
      image: this.validatedField?.value?.url,
      result: null,
      description: null,
      imageDragOver: false,
      galleryList: [],
      defaultStyle: {
        maxHeight: "64vh",
        maxWidth: "100%",
        borderRadius: "8px",
      },
      dragOverStyle: {
        maxHeight: "64vh",
        maxWidth: "100%",
        border: "3px dashed #4D7A98",
        borderRadius: "8px",
      },
    }
  }
}
</script>

<style scoped>

.inspiration-button {
  color: #FFFFFF;
  width: 100%;
  height: 40px;
  font-weight: bold;
  background-color: #4D7A98;
  border-radius: 5px;
  border: 2px solid #4D7A98;
}

.inspiration-button:hover {
  background-color: #253D5A;
  border: 2px solid #253D5A;
}

@media screen and (max-width: 850px) {
  .inspiration-button {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 52px;
  }
}

.image-card {
  display: flex;
  justify-content: center;
  height: 50vh;
}

.button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.card-content {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: #4D7A98;
}

.upload-icon {
  font-size: 1.5rem;
}

.upload-image {
  height: 80px;
}

.instruction-title {
  font-weight: bold;
  font-size: 20px;
}

.instruction-span {
  font-size: 16px;
}

.custom-bold {
  font-weight: bold;
  text-decoration: underline;
}
</style>